type PermissionGrant = string | { name?: string | null } | null | undefined;

export const SUPERUSER_PERMISSION = "*:*";
export const INTERNAL_ADMIN_PERMISSION = "internal:admin";

export const INTERNAL_ONLY_CONTENT_TYPES = [
  "application/vnd.pivotalgroup.automation.node.qualifier+json",
  "application/vnd.pivotalgroup.automation.node.weight+json",
  "application/vnd.pivotalgroup.automation.node.construct+json",
] as const;

export function normalizePermissions(
  granted: readonly PermissionGrant[] | null | undefined
): string[] {
  if (!granted?.length) {
    return [];
  }

  return granted
    .map((permission) =>
      typeof permission === "string" ? permission : permission?.name
    )
    .filter((permission): permission is string => !!permission?.trim())
    .map((permission) => permission.trim());
}

export function isInternalContentType(mimeType?: string | null): boolean {
  if (!mimeType) {
    return false;
  }

  return INTERNAL_ONLY_CONTENT_TYPES.some(
    (type) => type.toLowerCase() === mimeType.toLowerCase()
  );
}

function isInternalPermission(permission: string): boolean {
  if (!permission) {
    return false;
  }

  if (permission.toLowerCase().startsWith("internal:")) {
    return true;
  }

  if (!permission.toLowerCase().startsWith("content:")) {
    return false;
  }

  const withoutPrefix = permission.slice("content:".length);
  const lastColon = withoutPrefix.lastIndexOf(":");
  const mimeType =
    lastColon > 0 ? withoutPrefix.slice(0, lastColon) : withoutPrefix;

  return isInternalContentType(mimeType);
}

export function contentPermissionKey(
  mimeType: string,
  action: "read" | "write"
): string {
  return `content:${mimeType}:${action}`;
}

export function hasPermission(
  granted: readonly PermissionGrant[] | null | undefined,
  required: string
): boolean {
  if (!required) {
    return false;
  }

  const permissions = new Set(normalizePermissions(granted));

  if (permissions.has(required)) {
    return true;
  }

  if (isInternalPermission(required)) {
    return false;
  }

  if (permissions.has(SUPERUSER_PERMISSION)) {
    return true;
  }

  const lastColon = required.lastIndexOf(":");
  if (lastColon > 0) {
    if (permissions.has(`${required.slice(0, lastColon)}:*`)) {
      return true;
    }

    // Content-family wildcard: content:*:<action> / content:*:* grants the action over all
    // non-internal mimes (internal content already returned false above).
    if (required.startsWith("content:")) {
      const action = required.slice(lastColon + 1);
      if (
        permissions.has(`content:*:${action}`) ||
        permissions.has("content:*:*")
      ) {
        return true;
      }
    }
  }

  return false;
}

export function hasContentPermission(
  granted: readonly PermissionGrant[] | null | undefined,
  mimeType: string | null | undefined,
  action: "read" | "write"
): boolean {
  if (!mimeType) {
    return false;
  }

  return hasPermission(granted, contentPermissionKey(mimeType, action));
}

export function usePermissions() {
  return {
    SUPERUSER_PERMISSION,
    INTERNAL_ADMIN_PERMISSION,
    INTERNAL_ONLY_CONTENT_TYPES,
    normalizePermissions,
    isInternalContentType,
    contentPermissionKey,
    hasPermission,
    hasContentPermission,
  };
}
